// frontend/src/api.js - Complete API Client
import axios from "axios";

// ============================================
// API CONFIGURATION
// ============================================

const getApiUrl = () => {
  if (process.env.NODE_ENV === "production") {
    // Use the actual Render backend URL
    return (
      process.env.REACT_APP_API_URL ||
      "https://wrightagro-backend.onrender.com/api"
    );
  }
  return process.env.REACT_APP_API_URL || "http://localhost:5002/api";
};

const API = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================

API.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user?.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================
// RESPONSE INTERCEPTOR (Token Refresh)
// ============================================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await API.post("/auth/refresh-token");
        const { token } = refreshResponse.data;

        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (user) {
          user.token = token;
          localStorage.setItem("user", JSON.stringify(user));
        }

        processQueue(null, token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("user");
        localStorage.removeItem("cart");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      window.location.href = "/unauthorized";
    }

    return Promise.reject(error);
  },
);

// ============================================
// AUTH ENDPOINTS
// ============================================

export const login = (email, password) =>
  API.post("/auth/login", { email, password });

export const register = (userData) => API.post("/auth/register", userData);

export const getMe = () => API.get("/auth/me");

export const updateProfile = (data) => API.put("/auth/profile", data);

export const changePassword = (oldPassword, newPassword) =>
  API.put("/auth/change-password", { oldPassword, newPassword });

export const refreshToken = () => API.post("/auth/refresh-token");

export const logout = () => API.post("/auth/logout");

export const forgotPassword = (email) =>
  API.post("/auth/forgot-password", { email });

export const resetPassword = (token, password) =>
  API.post(`/auth/reset-password/${token}`, { password });

export const verifyEmail = (token) => API.get(`/auth/verify/${token}`);

// ============================================
// USER PREFERENCES
// ============================================

export const updateUserPreferences = (data) =>
  API.put("/users/preferences", data);

export const getMyPreOrders = () => API.get("/users/my-preorders");

// ============================================
// PRE-ORDER ENDPOINTS
// ============================================

export const createPreOrder = (data) => API.post("/preorders", data);

export const lookupPreOrders = (phone) =>
  API.get("/preorders/lookup", { params: { phone } });

export const getPreOrderById = (id) => API.get(`/preorders/${id}`);

export const cancelPreOrder = (id) => API.put(`/preorders/${id}/cancel`);

export const getAllPreOrders = (params) =>
  API.get("/preorders/admin/all", { params });

export const getPreOrderStats = () => API.get("/preorders/admin/stats");

export const updatePreOrderStatus = (id, data) =>
  API.put(`/preorders/${id}/status`, data);

// ============================================
// USER MANAGEMENT (Admin)
// ============================================

export const getUsers = (params) => API.get("/users/admin/all", { params });

export const getUserById = (id) => API.get(`/users/admin/${id}`);

export const updateUser = (id, data) => API.put(`/users/admin/${id}`, data);

export const deleteUser = (id) => API.delete(`/users/admin/${id}`);

export const getAdminUserStats = () => API.get("/users/admin/stats");

export const createTeamMember = (userData) =>
  API.post("/users/admin/create", userData);

export const toggleUserStatus = (userId) =>
  API.patch(`/users/admin/${userId}/toggle-status`);

// ============================================
// USER-SPECIFIC ENDPOINTS
// ============================================

export const getUserOrders = () => API.get("/orders/my-orders");

export const getMyUserStats = () => API.get("/users/my-stats");

// ============================================
// PRODUCT ENDPOINTS
// ============================================

export const getProducts = (params) => API.get("/products", { params });

export const getProductBySlug = (slug) => API.get(`/products/slug/${slug}`);

export const getProductById = (id) => API.get(`/products/${id}`);

export const createProduct = (data) => API.post("/products", data);

export const updateProduct = (id, data) => API.put(`/products/${id}`, data);

export const deleteProduct = (id) => API.delete(`/products/${id}`);

export const getFeaturedProducts = () => API.get("/products/featured/list");

export const getProductsByCategory = (category) =>
  API.get(`/products/category/${category}`);

export const getProductPriceTiers = (productId) =>
  API.get(`/products/${productId}/price-tiers`);

export const getBulkOptions = (productId) =>
  API.get(`/products/${productId}/bulk-options`);

// ============================================
// PRODUCT SIZE ENDPOINTS (NEW)
// ============================================

export const getAvailableSizes = () => API.get("/products/sizes");

export const getPreOrderSizes = () => API.get("/products/preorder-sizes");

// ============================================
// PRODUCT REACTIONS & COMMENTS
// ============================================

export const addReaction = (productId, reactionType) =>
  API.post("/reactions", { productId, reactionType });

export const getUserReaction = (productId) =>
  API.get(`/reactions/user/${productId}`);

export const getReactionCounts = (productId) =>
  API.get(`/reactions/counts/${productId}`);

export const getComments = (productId, params) =>
  API.get(`/comments/product/${productId}`, { params });

export const addComment = (productId, comment, parentCommentId = null) =>
  API.post("/comments", { productId, comment, parentCommentId });

export const markCommentHelpful = (commentId) =>
  API.post(`/comments/${commentId}/helpful`);

export const deleteComment = (commentId) =>
  API.delete(`/comments/${commentId}`);

// ============================================
// CART ENDPOINTS
// ============================================

export const getCart = () => API.get("/cart");

export const addToCart = (productId, quantity, fallbackData = null) => {
  const data = { productId, quantity };
  if (fallbackData) {
    data.name = fallbackData.name;
    data.price = fallbackData.price;
    data.unit = fallbackData.unit;
  }
  return API.post("/cart/add", data);
};

export const updateCartItem = (itemId, quantity) =>
  API.put("/cart/update", { itemId, quantity });

export const removeFromCart = (itemId) => API.delete(`/cart/item/${itemId}`);

export const clearCart = () => API.delete("/cart/clear");

// ============================================
// ORDER ENDPOINTS
// ============================================

export const createOrder = (orderData) => API.post("/orders", orderData);

export const getOrderById = (id) => API.get(`/orders/${id}`);

export const cancelOrder = (id) => API.put(`/orders/${id}/cancel`);

export const getAllOrders = (params) =>
  API.get("/orders/admin/all", { params });

export const updateOrderStatus = (id, status) =>
  API.put(`/orders/${id}/status`, { status });

export const createBulkOrder = (orderData) =>
  API.post("/orders", { ...orderData, orderType: "bulk_farmer" });

export const getBulkOrdersSummary = () => API.get("/orders/admin/bulk-summary");

// ============================================
// SHIPMENT ENDPOINTS
// ============================================

export const trackShipment = (trackingNumber) =>
  API.get(`/shipments/track/${trackingNumber}`);

export const getShipmentByOrder = (orderId) =>
  API.get(`/shipments/order/${orderId}`);

export const createShipment = (data) => API.post("/shipments", data);

export const getAllShipments = (params) =>
  API.get("/shipments/admin/all", { params });

export const updateShipmentStatus = (id, status, location, description) =>
  API.put(`/shipments/${id}/status`, { status, location, description });

// ============================================
// SUPPLIER ENDPOINTS
// ============================================

export const registerSupplier = (data) => API.post("/suppliers/register", data);

export const getSuppliers = (params) => API.get("/suppliers", { params });

export const getSupplierById = (id) => API.get(`/suppliers/${id}`);

export const updateSupplier = (id, data) => API.put(`/suppliers/${id}`, data);

export const approveSupplier = (id) => API.put(`/suppliers/${id}/approve`);

export const deleteSupplier = (id) => API.delete(`/suppliers/${id}`);

// ============================================
// INVENTORY ENDPOINTS
// ============================================

export const createInventoryFromShipment = (data) =>
  API.post("/inventory/from-shipment", data);

export const getInventory = (params) => API.get("/inventory", { params });

export const getInventoryById = (id) => API.get(`/inventory/${id}`);

export const updateInventory = (id, data) => API.put(`/inventory/${id}`, data);

export const deleteInventory = (id) => API.delete(`/inventory/${id}`);

export const getLowStockInventory = () => API.get("/inventory/low-stock");

export const getInventoryStats = () => API.get("/inventory/stats");

// ============================================
// CONTACT ENDPOINTS
// ============================================

export const createContactMessage = (data) => API.post("/contact", data);

export const getContactMessages = (params) => API.get("/contact", { params });

export const getContactMessageById = (id) => API.get(`/contact/${id}`);

export const respondToContactMessage = (id, data) =>
  API.put(`/contact/${id}/respond`, data);

export const updateMessageStatus = (id, data) =>
  API.put(`/contact/${id}/status`, data);

// ============================================
// CHAT ENDPOINTS
// ============================================

export const createChatSession = () => API.post("/chat/session");

export const sendChatMessage = (sessionId, message) =>
  API.post(`/chat/${sessionId}/message`, { message });

export const getChatHistory = (sessionId) =>
  API.get(`/chat/${sessionId}/history`);

export const rateChat = (sessionId, rating) =>
  API.post(`/chat/${sessionId}/rate`, { rating });

export const endChatSession = (sessionId) => API.put(`/chat/${sessionId}/end`);

// ============================================
// TESTIMONIAL ENDPOINTS
// ============================================

export const getApprovedTestimonials = (params) =>
  API.get("/testimonials/approved", { params });

export const createTestimonial = (data) => API.post("/testimonials", data);

export const getUserTestimonials = () =>
  API.get("/testimonials/my-testimonials");

export const updateTestimonial = (id, data) =>
  API.put(`/testimonials/${id}`, data);

export const deleteTestimonial = (id) => API.delete(`/testimonials/${id}`);

export const markTestimonialHelpful = (id) =>
  API.post(`/testimonials/${id}/helpful`);

export const getAllTestimonials = (params) =>
  API.get("/testimonials/admin/all", { params });

export const getTestimonialStats = () => API.get("/testimonials/admin/stats");

export const approveTestimonial = (id) =>
  API.put(`/testimonials/admin/${id}/approve`);

export const rejectTestimonial = (id, data) =>
  API.put(`/testimonials/admin/${id}/reject`, data);

export const featureTestimonial = (id) =>
  API.put(`/testimonials/admin/${id}/feature`);

export const adminRespondToTestimonial = (id, response) =>
  API.post(`/testimonials/admin/${id}/respond`, { response });

// ============================================
// PAYMENT ENDPOINTS
// ============================================

export const getPaymentMethods = () => API.get("/payments/methods");

export const addPaymentMethod = (data) => API.post("/payments/methods", data);

export const updatePaymentMethod = (id, data) =>
  API.put(`/payments/methods/${id}`, data);

export const removePaymentMethod = (id) =>
  API.delete(`/payments/methods/${id}`);

export const setDefaultPaymentMethod = (id) =>
  API.put(`/payments/methods/${id}/default`);

export const getPayoutHistory = () => API.get("/payments/payouts");

export const requestPayout = (data) => API.post("/payments/payouts", data);

export const getPayoutStats = () => API.get("/payments/payouts/stats");

// ============================================
// DASHBOARD ENDPOINTS
// ============================================

export const getDashboardStats = () => API.get("/dashboard/stats");

export const getRecentOrders = (limit = 10) =>
  API.get(`/dashboard/recent-orders?limit=${limit}`);

export const getLowStockProducts = (threshold = 10) =>
  API.get(`/dashboard/low-stock?threshold=${threshold}`);

export const getAnalytics = (period = "30d") =>
  API.get(`/dashboard/analytics?period=${period}`);

// ============================================
// EXPORT DEFAULT
// ============================================

export default API;